import React from 'react'
import { Typography } from '~/components/common/Typography'
import { CommandType, UsedCommandType } from '~/constants/commands'
import { commandTypeToJSON } from '~/protos/agents'

interface SkillsViewProps {
  skills: {
    icon: React.ReactNode
    label: string
    description: string
    visible?: boolean
    commandType?: CommandType
    beta?: boolean
  }[]
  onSkillClick: (skillLabel: string) => void
}

const SKILL_BUTTON_CLASS =
  'group flex items-start md:items-center md:justify-between gap-3 py-2 px-3 rounded-xl hover:bg-tokens-neutral-fill-fill-4 transition-colors cursor-pointer text-left w-full select-none'

function SkillButtonContent({ skill }: { skill: SkillsViewProps['skills'][number] }) {
  return (
    <>
      <div className='flex items-start md:items-center gap-3 min-w-0'>
        <div className='shrink-0 pt-[2px] md:pt-0'>{skill.icon}</div>
        <div className='flex items-center gap-2 min-w-0'>
          <Typography
            fontFamily='geist'
            fontSize='bodyMd'
            fontWeight='semibold'
            className='text-tokens-neutral-text-primary'
          >
            {skill.label}
          </Typography>
          {skill.beta && (
            <Typography
              fontFamily='geistMono'
              fontSize='labelSm'
              fontWeight='semibold'
              className='shrink-0 rounded-[6px] bg-tokens-primary-4 px-1.5 py-0.5 text-tokens-neutral-bg-white'
            >
              Beta
            </Typography>
          )}
        </div>
      </div>
      <div className='hidden md:block opacity-0 group-hover:opacity-100 transition-opacity duration-200 min-w-0'>
        <Typography
          fontFamily='geist'
          fontSize='bodyMd'
          fontWeight='normal'
          className='text-tokens-neutral-text-tertiary'
        >
          {skill.description}
        </Typography>
      </div>
    </>
  )
}

export function SkillsView({ skills, onSkillClick }: SkillsViewProps) {
  return (
    <div className='bg-tokens-neutral-bg-white rounded-3xl shadow-popover p-5 w-full'>
      <div className='flex flex-col gap-6'>
        <div className='flex flex-col gap-4'>
          {skills.map((skill, index) => {
            const commandType = skill.commandType as UsedCommandType
            return (
              <button
                key={index}
                onClick={() => onSkillClick(skill.label)}
                className={SKILL_BUTTON_CLASS}
                data-testid={`skill-${commandTypeToJSON(commandType)}`}
              >
                <SkillButtonContent skill={skill} />
              </button>
            )
          })}
        </div>
      </div>
    </div>
  )
}
